import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular';
import { DX_USD_CURRENCY_FORMAT } from '@ag-common-lib/lib/constants/currency.constants';
import type {
  DashboardRevenueProjectionBonusDetailsResponse,
  DashboardRevenueProjectionCarrierResponse,
  DashboardRevenueProjectionMethodDto,
  DashboardViewDto,
} from 'ag-common-lib/lib/contracts/reporting-api';
import { DashboardReportingApiService } from '../../services/dashboard-reporting-api.service';
import {
  PROJECTION_METHOD_OPTIONS,
  type ProjectionMethodOption,
} from '../projection-method-select/projection-method-options';

export {
  PROJECTION_DATA_NOTE,
  PROJECTION_METHOD_OPTIONS,
  type ProjectionMethodOption,
} from '../projection-method-select/projection-method-options';

type Confidence = DashboardRevenueProjectionCarrierResponse['confidence'];

/**
 * Year-end revenue projection and 7.5% bonus estimate, per carrier.
 *
 * Self-contained: the component fetches its own payload, so a consumer only has to place
 * the element. `view` scopes the request and defaults to the org-wide figure.
 *
 * Two things this grid deliberately puts on screen rather than hiding:
 *
 *  - **The as-of week.** `ytd` is SETTLED volume, cut back from today to dodge the carrier
 *    reporting lag, so it is *not* "everything in the table". Without the caption someone
 *    reconciles it against a raw YTD number, finds a gap, and distrusts the whole widget.
 *  - **Per-row error.** Accuracy varies enormously by carrier — the largest projects to
 *    ~±5%, sparse ones to ±30-60%. A bonus figure with no error bar invites decisions the
 *    number cannot support, so `errorPct`/confidence travel next to it.
 */
@Component({
  selector: 'ag-revenue-projection-grid',
  templateUrl: './revenue-projection-grid.component.html',
  styleUrls: ['./revenue-projection-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RevenueProjectionGridComponent {
  // The .scss scopes everything under this class, so the host must carry it.
  @HostBinding('class') className = 'revenue-projection-grid';

  @ViewChild(DxDataGridComponent) private dataGrid?: DxDataGridComponent;

  private changeDetectorRef = inject(ChangeDetectorRef);
  private dashboardReportingApiService = inject(DashboardReportingApiService);

  /**
   * Scopes the request. 'all' is org-wide and the endpoint enforces PORTAL_ADMIN for it, so
   * consumers are expected to gate access before placing the component.
   */
  readonly view = input<DashboardViewDto>('all');
  /** Shown in the grid's own toolbar; overridable since the component is shared. */
  readonly title = input<string>('Revenue Projection');

  protected readonly projection = signal<DashboardRevenueProjectionBonusDetailsResponse | null>(null);
  protected readonly isLoading = signal<boolean>(true);
  protected readonly hasFailed = signal<boolean>(false);

  /**
   * Deliberately not persisted and not an input: `credibility` is the answer, and the other
   * two are a what-if you run and leave. Remembering a choice across sessions would let
   * someone read `runrate` numbers for months without noticing they had opted out of the
   * accurate method.
   */
  protected readonly method = signal<DashboardRevenueProjectionMethodDto>('credibility');

  constructor() {
    // Reacts to `view` and `method` so the component reloads when either changes, and covers
    // the initial read (inputs are not bound yet at construction time).
    effect(
      () => {
        const view = this.view();
        const method = this.method();
        void this.load(view, method);
      },
      { allowSignalWrites: true },
    );

    // The grid's own load panel only appears while the grid itself is fetching, and ours is
    // fed a plain array — so nothing would ever show it. beginCustomLoading is how the rest
    // of the app drives that panel by hand (see products / addresses-management).
    effect(() => {
      const instance = this.dataGrid?.instance;
      if (!instance) {
        return;
      }
      if (this.isLoading()) {
        instance.beginCustomLoading('Loading...');
      } else {
        instance.endCustomLoading();
      }
    });
  }

  protected readonly DX_USD_CURRENCY_FORMAT = DX_USD_CURRENCY_FORMAT;

  private async load(view: DashboardViewDto, method: DashboardRevenueProjectionMethodDto): Promise<void> {
    this.isLoading.set(true);
    this.hasFailed.set(false);

    try {
      // No year argument: the projection is only meaningful for the year in progress, and
      // the backend derives the as-of week itself (settled weeks only, lag excluded).
      const response = await this.dashboardReportingApiService.getRevenueProjectionBonus({ view, method });
      this.projection.set(response.data);
    } catch {
      // A dashboard widget must not take the board down with it — the user can still work.
      this.hasFailed.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  protected retry = (): void => {
    void this.load(this.view(), this.method());
  };

  /**
   * What the numbers on screen were actually computed with, straight from the response —
   * as opposed to `method`, which is what was last *requested*. Mid-refetch the two differ.
   *
   * Only the caveat below the grid may read this. The picker itself must bind to `method`:
   * binding the control to the response makes it lag its own input, so change detection
   * writes the previous method back into the box, which fires onValueChanged again and
   * reverts the user's choice. That loop is why the dropdown appeared to do nothing.
   */
  protected readonly computedMethod = computed<DashboardRevenueProjectionMethodDto | null>(
    () => this.projection()?.method ?? null,
  );

  /** A non-default method is a deliberate what-if; say so next to the numbers. */
  protected readonly isNonDefaultMethod = computed(
    () => this.computedMethod() !== null && this.computedMethod() !== 'credibility',
  );

  protected readonly computedMethodOption = computed<ProjectionMethodOption | undefined>(() =>
    PROJECTION_METHOD_OPTIONS.find(option => option.value === this.computedMethod()),
  );

  protected onMethodChanged = (value: DashboardRevenueProjectionMethodDto): void => {
    if (!value || value === this.method()) {
      return; // dxSelectBox also fires on programmatic writes; don't refetch our own echo
    }
    this.method.set(value); // the constructor effect picks this up and reloads
  };

  protected readonly rows = computed<DashboardRevenueProjectionCarrierResponse[]>(() => this.projection()?.rows ?? []);
  protected readonly summary = computed(() => this.projection()?.summary ?? null);

  /** A projection needs history and at least one settled week; below that the API declines. */
  protected readonly hasProjection = computed(() => !!this.projection()?.hasProjection);

  protected readonly ratePct = computed(() => (this.projection()?.rate ?? 0) * 100);

  /** How far the bottom-up sum differs from the headline — a cheap "is the mix odd?" signal. */
  protected readonly deltaAbPct = computed(() => this.summary()?.deltaABPct ?? 0);
  protected readonly isDeltaNotable = computed(() => Math.abs(this.deltaAbPct()) >= 10);

  protected readonly vsPrevYearPct = computed(() => {
    const summary = this.summary();
    if (!summary?.prevYearBonus) {
      return null;
    }
    return (summary.totalBonus / summary.prevYearBonus - 1) * 100;
  });

  protected confidenceLabel = (row: DashboardRevenueProjectionCarrierResponse): string => {
    if (row.errorPct === null) {
      return 'no history';
    }
    return `±${row.errorPct.toFixed(1)}%`;
  };

  /** Half-width of the total's band, as a ± percentage — the total's own accuracy. */
  protected readonly totalAccuracyPct = computed(() => {
    const summary = this.summary();
    if (!summary?.totalBonus) {
      return null;
    }
    const spread = Math.max(summary.totalBonusHigh - summary.totalBonus, summary.totalBonus - summary.totalBonusLow);
    return (spread / summary.totalBonus) * 100;
  });

  protected readonly totalConfidence = computed<Confidence>(() => {
    const accuracy = this.totalAccuracyPct();
    if (accuracy === null) {
      return 'none';
    }
    // A wide A-vs-B gap means the carrier mix is behaving unusually — that caps confidence
    // regardless of how tight the band looks.
    if (this.isDeltaNotable()) {
      return 'medium';
    }
    if (accuracy <= 5) {
      return 'high';
    }
    return accuracy <= 10 ? 'medium' : 'low';
  });

  // One popover for both the per-row icons and the summary icon; `popoverKind` picks the body.
  protected isAccuracyPopoverVisible = false;
  protected accuracyPopoverTarget: EventTarget | Element | null = null;
  protected accuracyPopoverRow: DashboardRevenueProjectionCarrierResponse | null = null;
  protected popoverKind: 'row' | 'summary' | null = null;

  protected openAccuracyPopover = (event: Event, row: DashboardRevenueProjectionCarrierResponse): void => {
    event?.stopPropagation();
    this.popoverKind = 'row';
    this.accuracyPopoverRow = row;
    this.accuracyPopoverTarget = event.target;
    this.isAccuracyPopoverVisible = true;
  };

  /**
   * dxi-total-item takes no template — only text options — so the summary's icon has to be
   * appended to the rendered cell. onCellPrepared is where the app already reaches for a
   * summary/total cell element.
   */
  protected onCellPrepared = (event: any): void => {
    if (event?.rowType !== 'totalFooter' || event?.column?.dataField !== 'bonus') {
      return;
    }
    const cell: HTMLElement | undefined = event.cellElement;
    if (!cell || cell.querySelector('.revenue-projection-grid__accuracy-icon')) {
      return; // onCellPrepared re-fires on every repaint; don't stack icons
    }

    // DevExtreme wraps the total in a block-level .dx-datagrid-summary-item, so appending to
    // the cell itself pushed the icon onto its own line. Put it inside that element instead.
    const host: HTMLElement = cell.querySelector('.dx-datagrid-summary-item') ?? cell;

    const icon = document.createElement('i');
    icon.className = `dx-icon-info revenue-projection-grid__accuracy-icon revenue-projection-grid__accuracy-icon--${this.totalConfidence()}`;
    icon.setAttribute('aria-label', 'Expected bonus accuracy and cross-check');
    const open = (domEvent: Event) => {
      domEvent.stopPropagation();
      this.popoverKind = 'summary';
      this.accuracyPopoverRow = null;
      this.accuracyPopoverTarget = icon;
      this.isAccuracyPopoverVisible = true;
      this.changeDetectorRef.markForCheck(); // outside Angular's template bindings
    };
    icon.addEventListener('mouseenter', open);
    icon.addEventListener('click', open);
    host.appendChild(icon);
  };
}
